/**
 * Cadastro de funcionários com wizard 5 abas.
 * 1. Dados Básicos  2. Horário  3. Cargos  4. Exames Periódicos  5. Dados Adicionais
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  funcApi, politicaApi, escalaApi, empresaApi,
  getErrorMessage, isPlanLimitError,
} from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type {
  Funcionario,
  FuncionarioDependente,
  FuncionarioAtestado,
  FuncionarioExame,
  FuncionarioHistoricoCargo,
  TipoContrato,
  TipoAtestado,
  TipoExame,
  ResultadoExame,
} from '../../types/api'
import { formatCpf } from '../../types/api'

// ── Constantes ────────────────────────────────────────────────────────────────

const TIPO_CONTRATO_LABELS: Record<TipoContrato, string> = {
  CLT: 'CLT', PJ: 'Prestador (PJ)', ESTAGIO: 'Estágio', APRENDIZ: 'Aprendiz',
}

const PARENTESCOS = [
  'CONJUGE','COMPANHEIRO','FILHO','FILHA','PAI','MAE','IRMAO','IRMA',
  'TUTELADO','ENTEADO','OUTROS',
]
const PARENTESCO_LABELS: Record<string, string> = {
  CONJUGE: 'Cônjuge', COMPANHEIRO: 'Companheiro(a)', FILHO: 'Filho',
  FILHA: 'Filha', PAI: 'Pai', MAE: 'Mãe', IRMAO: 'Irmão',
  IRMA: 'Irmã', TUTELADO: 'Tutelado', ENTEADO: 'Enteado(a)', OUTROS: 'Outros',
}

const TIPOS_ATESTADO: { value: TipoAtestado; label: string }[] = [
  { value: 'DOENCA', label: 'Doença' },
  { value: 'ACIDENTE_TRABALHO', label: 'Acidente do Trabalho' },
  { value: 'ACIDENTE_PESSOAL', label: 'Acidente Pessoal' },
  { value: 'MATERNIDADE', label: 'Licença Maternidade' },
  { value: 'PATERNIDADE', label: 'Licença Paternidade' },
  { value: 'ACOMPANHANTE', label: 'Acompanhante' },
  { value: 'OUTROS', label: 'Outros' },
]

const TIPOS_EXAME: { value: TipoExame; label: string }[] = [
  { value: 'ADMISSIONAL', label: 'Admissional' },
  { value: 'PERIODICO', label: 'Periódico' },
  { value: 'DEMISSIONAL', label: 'Demissional' },
  { value: 'RETORNO', label: 'Retorno ao Trabalho' },
  { value: 'MUDANCA_FUNCAO', label: 'Mudança de Função' },
]

const RESULTADOS_EXAME: { value: ResultadoExame; label: string; color: string }[] = [
  { value: 'APTO', label: 'Apto', color: 'var(--color-success)' },
  { value: 'APTO_RESTRICAO', label: 'Apto c/ Restrição', color: 'var(--color-warning)' },
  { value: 'INAPTO', label: 'Inapto', color: 'var(--color-danger)' },
]

const WIZARD_STEPS = [
  { id: 1, label: 'Dados Básicos' },
  { id: 2, label: 'Horário' },
  { id: 3, label: 'Cargos' },
  { id: 4, label: 'Exames Periódicos' },
  { id: 5, label: 'Dados Adicionais' },
]

const FORM_EMPTY = {
  nome: '', cpf: '', pis: '', empresa_id: '', rg: '', cracha: '',
  registro_folha: '', celular: '', email: '', matricula: '', cargo: '',
  departamento: '', local_trabalho: '', data_admissao: '', data_demissao: '',
  aviso_previo_data: '', jornada_horas_dia: '8', tipo_contrato: 'CLT' as TipoContrato,
  politica_id: '', escala_id: '', ativo: true,
}

const DEP_EMPTY = { nome: '', parentesco: 'FILHO', data_nascimento: '', cpf: '', ir_declarado: false }
const ATE_EMPTY = { data_inicio: '', data_fim: '', dias_afastamento: '1', tipo: 'DOENCA' as TipoAtestado, cid: '', medico: '', crm: '', observacao: '' }
const EXAME_EMPTY = { tipo: 'ADMISSIONAL' as TipoExame, data_exame: '', data_vencimento: '', resultado: 'APTO' as ResultadoExame, medico: '', observacao: '' }
const HIST_EMPTY = { cargo: '', departamento: '', empresa_id: '', data_inicio: '', data_fim: '', salario: '', motivo_alteracao: '' }

// ── Componentes auxiliares ────────────────────────────────────────────────────

function F({ label, children, opt, span2 }: { label: string; children: React.ReactNode; opt?: boolean; span2?: boolean }) {
  return (
    <div style={{ gridColumn: span2 ? '1/-1' : undefined }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{opt && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}

function SecHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ gridColumn: '1/-1', fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-gray-500)', borderBottom: '1px solid var(--color-gray-200)', paddingBottom: 'var(--space-1)', marginTop: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
      {children}
    </div>
  )
}

function EmptyState({ msg }: { msg: string }) {
  return <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)', border: '1px dashed var(--color-gray-300)', borderRadius: 'var(--radius-md)' }}>{msg}</div>
}

// ── Wizard Steps ──────────────────────────────────────────────────────────────

function WizardNav({ step, onChange }: { step: number; onChange: (s: number) => void }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--color-gray-200)', marginBottom: 'var(--space-6)', gap: 0 }}>
      {WIZARD_STEPS.map((s) => {
        const active = s.id === step
        const done = s.id < step
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange(s.id)}
            style={{
              flex: 1, padding: 'var(--space-3) var(--space-2)',
              fontSize: 'var(--font-size-xs)', fontWeight: active ? 700 : 500,
              color: active ? 'var(--color-primary)' : done ? 'var(--color-success)' : 'var(--color-gray-500)',
              borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer', textAlign: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <span style={{
              width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
              background: active ? 'var(--color-primary)' : done ? 'var(--color-success)' : 'var(--color-gray-200)',
              color: active || done ? 'white' : 'var(--color-gray-500)',
              flexShrink: 0,
            }}>{done ? '✓' : s.id}</span>
            <span style={{ display: 'none' /* hide label on mobile */ }}>{s.label}</span>
            <span style={{ display: 'block' }}>{s.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Step 1: Dados Básicos ─────────────────────────────────────────────────────

function Step1({ form, setForm, isEdit, empresas }: { form: typeof FORM_EMPTY; setForm: React.Dispatch<React.SetStateAction<typeof FORM_EMPTY>>; isEdit: boolean; empresas: { id: string; nome: string }[] }) {
  const s = (field: keyof typeof FORM_EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }))

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
      <SecHeader>Identificação</SecHeader>
      <F label="Nome completo" span2>
        <input className="input" value={form.nome} onChange={s('nome')} placeholder="Nome completo do funcionário" required />
      </F>
      <F label="Empresa / Filial">
        <select className="input" value={form.empresa_id} onChange={s('empresa_id')} required>
          <option value="">— Selecione a empresa —</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </F>
      <F label="Matrícula" opt>
        <input className="input" value={form.matricula} onChange={s('matricula')} placeholder="Número da matrícula" />
      </F>
      <F label="CPF (somente números)">
        <input className="input" value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: e.target.value.replace(/\D/g, '') }))} placeholder="12345678901" maxLength={11} required disabled={isEdit} />
      </F>
      <F label="PIS/PASEP (NIT)">
        <input className="input" value={form.pis} onChange={e => setForm(f => ({ ...f, pis: e.target.value.replace(/\D/g, '') }))} placeholder="12345678901" maxLength={11} required />
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', display: 'block', marginTop: 2 }}>
          Obrigatório — identifica unicamente o trabalhador (LGPD / CAGED)
        </span>
      </F>
      <F label="RG" opt>
        <input className="input" value={form.rg} onChange={s('rg')} placeholder="00.000.000-0" />
      </F>
      <F label="Crachá / Digital" opt>
        <input className="input" value={form.cracha} onChange={s('cracha')} placeholder="Número do crachá ou ID biométrico" />
      </F>
      <F label="Registro Folha de Pagamento" opt>
        <input className="input" value={form.registro_folha} onChange={s('registro_folha')} placeholder="Ex: 00123" />
      </F>

      <SecHeader>Contato</SecHeader>
      <F label="Celular" opt>
        <input className="input" value={form.celular} onChange={s('celular')} placeholder="(11) 99999-9999" />
      </F>
      <F label="E-mail" opt>
        <input className="input" type="email" value={form.email} onChange={s('email')} placeholder="funcionario@empresa.com.br" />
      </F>

      <SecHeader>Departamento e Cargo</SecHeader>
      <F label="Cargo" opt>
        <input className="input" value={form.cargo} onChange={s('cargo')} placeholder="Ex: Operador de Produção" />
      </F>
      <F label="Departamento" opt>
        <input className="input" value={form.departamento} onChange={s('departamento')} placeholder="Ex: Produção" />
      </F>
      <F label="Local de trabalho" opt span2>
        <input className="input" value={form.local_trabalho} onChange={s('local_trabalho')} placeholder="Ex: Filial SP, Fábrica A, Obra 03" />
      </F>

      <SecHeader>Vínculo Empregatício</SecHeader>
      <F label="Data de admissão">
        <input className="input" type="date" value={form.data_admissao} onChange={s('data_admissao')} required />
      </F>
      <F label="Tipo de contrato">
        <select className="input" value={form.tipo_contrato} onChange={e => setForm(f => ({ ...f, tipo_contrato: e.target.value as TipoContrato }))}>
          {Object.entries(TIPO_CONTRATO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </F>
      <F label="Jornada contratual (h/dia)">
        <input className="input" type="number" min="1" max="12" step="0.5" value={form.jornada_horas_dia} onChange={s('jornada_horas_dia')} />
      </F>
      <F label="Aviso Prévio — data" opt>
        <input className="input" type="date" value={form.aviso_previo_data} onChange={s('aviso_previo_data')} />
      </F>
      <F label="Data de demissão" opt>
        <input className="input" type="date" value={form.data_demissao} onChange={s('data_demissao')} />
      </F>

      {isEdit && (
        <>
          <SecHeader>Status</SecHeader>
          <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!form.ativo} onChange={e => setForm(f => ({ ...f, ativo: !e.target.checked }))} />
              <span style={{ fontSize: 'var(--font-size-sm)' }}>Funcionário inativo (desativado)</span>
            </label>
          </div>
        </>
      )}
    </div>
  )
}

// ── Step 2: Horário ───────────────────────────────────────────────────────────

function Step2({
  form, setForm, politicas, escalas, funcId,
}: {
  form: typeof FORM_EMPTY
  setForm: React.Dispatch<React.SetStateAction<typeof FORM_EMPTY>>
  politicas: { id: string; nome: string; ativo: boolean }[]
  escalas: { id: string; nome: string; tipo: string; ativo: boolean; entrada: string | null; saida: string | null }[]
  funcId: string | null
}) {
  const polAtiva = politicas.find(p => p.id === form.politica_id)
  const escAtiva = escalas.find(e => e.id === form.escala_id)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="card" style={{ backgroundColor: 'var(--color-gray-50)' }}>
        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Quadro de Horas (Escala)</h3>
        <select className="input" value={form.escala_id} onChange={e => setForm(f => ({ ...f, escala_id: e.target.value }))} style={{ marginBottom: 'var(--space-3)' }}>
          <option value="">— Sem escala definida —</option>
          {escalas.filter(e => e.ativo).map(e => (
            <option key={e.id} value={e.id}>{e.nome} ({e.tipo}){e.entrada ? ` — ${e.entrada}–${e.saida}` : ''}</option>
          ))}
        </select>
        {escAtiva && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <span><b>Tipo:</b> {escAtiva.tipo}</span>
            {escAtiva.entrada && <span><b>Horário:</b> {escAtiva.entrada} – {escAtiva.saida}</span>}
          </div>
        )}
      </div>

      <div className="card" style={{ backgroundColor: 'var(--color-gray-50)' }}>
        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Políticas Agrupadas (CLT / CCT)</h3>
        <select className="input" value={form.politica_id} onChange={e => setForm(f => ({ ...f, politica_id: e.target.value }))} style={{ marginBottom: 'var(--space-3)' }}>
          <option value="">— Usar padrão CLT (clt_rules.yaml) —</option>
          {politicas.filter(p => p.ativo).map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        {polAtiva && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)' }}>
            Política <b>{polAtiva.nome}</b> aplicada — regras de hora extra, noturno, DSR e banco de horas definidas nela.
          </p>
        )}
        {!form.politica_id && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
            Sem política vinculada — serão aplicados os defaults do art. 58/59/73 CLT (50% dia útil, 20% noturno, DSR).
          </p>
        )}
      </div>

      <div className="card" style={{ backgroundColor: 'var(--color-blue-50, #eff6ff)' }}>
        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>Banco de Horas</h3>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)' }}>
          Configurado na Política de Trabalho vinculada. Individual: prazo 6 meses (art. 59 §6° CLT). Coletivo: 12 meses via ACT/CCT (art. 59 §2°).
          {form.politica_id ? ' ✓ Gerenciado pela política selecionada.' : ' Configure uma política para habilitar.'}
        </p>
      </div>

      {funcId && (
        <div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', textAlign: 'center' }}>
            Histórico de escalas e políticas anteriores é registrado automaticamente ao salvar alterações.
          </p>
        </div>
      )}
    </div>
  )
}

// ── Step 3: Cargos ────────────────────────────────────────────────────────────

function Step3({ funcId, empresas }: { funcId: string | null; empresas: { id: string; nome: string }[] }) {
  const qc = useQueryClient()
  const { data: historico = [], isLoading } = useQuery({
    queryKey: ['func-hist-cargo', funcId],
    queryFn: () => funcApi.historicoCargo.list(funcId!),
    enabled: !!funcId,
  })

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(HIST_EMPTY)
  const [err, setErr] = useState<string | null>(null)

  const createMut = useMutation({
    mutationFn: () => funcApi.historicoCargo.create(funcId!, {
      cargo: form.cargo,
      departamento: form.departamento || undefined,
      empresa_id: form.empresa_id || undefined,
      data_inicio: form.data_inicio,
      data_fim: form.data_fim || undefined,
      salario: form.salario ? Number(form.salario) : undefined,
      motivo_alteracao: form.motivo_alteracao || undefined,
    } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['func-hist-cargo', funcId] }); setShowForm(false); setForm(HIST_EMPTY) },
    onError: e => setErr(getErrorMessage(e)),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => funcApi.historicoCargo.delete(funcId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['func-hist-cargo', funcId] }),
  })

  if (!funcId) return <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-gray-400)' }}>Salve os dados básicos primeiro para registrar o histórico de cargos.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>Histórico de Cargos</h3>
        <button className="btn btn-primary" style={{ fontSize: 'var(--font-size-sm)' }} onClick={() => setShowForm(true)}>+ Registrar cargo</button>
      </div>

      {showForm && (
        <div className="card" style={{ border: '1px solid var(--color-primary)' }}>
          <h4 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>Novo registro de cargo</h4>
          {err && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <F label="Cargo">
              <input className="input" value={form.cargo} onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} placeholder="Ex: Analista de TI" required />
            </F>
            <F label="Departamento" opt>
              <input className="input" value={form.departamento} onChange={e => setForm(f => ({ ...f, departamento: e.target.value }))} />
            </F>
            <F label="Empresa" opt>
              <select className="input" value={form.empresa_id} onChange={e => setForm(f => ({ ...f, empresa_id: e.target.value }))}>
                <option value="">—</option>
                {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </F>
            <F label="Salário (R$)" opt>
              <input className="input" type="number" min="0" step="0.01" value={form.salario} onChange={e => setForm(f => ({ ...f, salario: e.target.value }))} placeholder="0,00" />
            </F>
            <F label="Data início">
              <input className="input" type="date" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} required />
            </F>
            <F label="Data fim" opt>
              <input className="input" type="date" value={form.data_fim} onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))} />
            </F>
            <F label="Motivo da alteração" opt span2>
              <input className="input" value={form.motivo_alteracao} onChange={e => setForm(f => ({ ...f, motivo_alteracao: e.target.value }))} placeholder="Ex: Promoção, transferência, mudança salarial" />
            </F>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <button className="btn btn-primary" disabled={createMut.isPending} onClick={() => createMut.mutate()}>{createMut.isPending ? 'Salvando…' : 'Salvar'}</button>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setErr(null) }}>Cancelar</button>
          </div>
        </div>
      )}

      {isLoading ? <PageLoading /> : historico.length === 0 ? (
        <EmptyState msg="Nenhum cargo registrado. Use o botão acima para registrar." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {historico.map((h: FuncionarioHistoricoCargo) => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-3)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{h.cargo}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                  {h.departamento && <span>{h.departamento} · </span>}
                  {new Date(h.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')}
                  {h.data_fim ? ` até ${new Date(h.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}` : ' — atual'}
                  {h.salario ? ` · R$ ${h.salario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                </div>
                {h.motivo_alteracao && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', marginTop: 2 }}>{h.motivo_alteracao}</div>}
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', padding: '2px 6px' }}
                onClick={() => { if (confirm('Remover este registro?')) deleteMut.mutate(h.id) }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Step 4: Exames Periódicos ─────────────────────────────────────────────────

function Step4({ funcId }: { funcId: string | null }) {
  const qc = useQueryClient()
  const { data: exames = [], isLoading } = useQuery({
    queryKey: ['func-exames', funcId],
    queryFn: () => funcApi.exames.list(funcId!),
    enabled: !!funcId,
  })

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EXAME_EMPTY)
  const [err, setErr] = useState<string | null>(null)

  const createMut = useMutation({
    mutationFn: () => funcApi.exames.create(funcId!, {
      tipo: form.tipo,
      data_exame: form.data_exame,
      data_vencimento: form.data_vencimento || undefined,
      resultado: form.resultado,
      medico: form.medico || undefined,
      observacao: form.observacao || undefined,
    } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['func-exames', funcId] }); setShowForm(false); setForm(EXAME_EMPTY) },
    onError: e => setErr(getErrorMessage(e)),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => funcApi.exames.delete(funcId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['func-exames', funcId] }),
  })

  if (!funcId) return <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-gray-400)' }}>Salve os dados básicos primeiro para registrar exames.</div>

  const vencendoEm30 = (exames as FuncionarioExame[]).filter(e => {
    if (!e.data_vencimento) return false
    const diff = (new Date(e.data_vencimento).getTime() - Date.now()) / 86400000
    return diff >= 0 && diff <= 30
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {vencendoEm30.length > 0 && (
        <div className="alert alert-warning" style={{ fontSize: 'var(--font-size-sm)' }}>
          <b>Atenção:</b> {vencendoEm30.length} exame(s) vencendo nos próximos 30 dias.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>Exames Periódicos</h3>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>NR-7 PCMSO — Programa de Controle Médico de Saúde Ocupacional</p>
        </div>
        <button className="btn btn-primary" style={{ fontSize: 'var(--font-size-sm)' }} onClick={() => setShowForm(true)}>+ Registrar exame</button>
      </div>

      {showForm && (
        <div className="card" style={{ border: '1px solid var(--color-primary)' }}>
          <h4 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>Novo exame</h4>
          {err && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-3)' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <F label="Tipo de exame">
              <select className="input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as TipoExame }))}>
                {TIPOS_EXAME.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </F>
            <F label="Resultado">
              <select className="input" value={form.resultado} onChange={e => setForm(f => ({ ...f, resultado: e.target.value as ResultadoExame }))}>
                {RESULTADOS_EXAME.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </F>
            <F label="Data do exame">
              <input className="input" type="date" value={form.data_exame} onChange={e => setForm(f => ({ ...f, data_exame: e.target.value }))} required />
            </F>
            <F label="Validade (vencimento)" opt>
              <input className="input" type="date" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} />
            </F>
            <F label="Médico responsável" opt>
              <input className="input" value={form.medico} onChange={e => setForm(f => ({ ...f, medico: e.target.value }))} placeholder="Dr. Nome Completo" />
            </F>
            <F label="Observação" opt>
              <input className="input" value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
            </F>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <button className="btn btn-primary" disabled={createMut.isPending} onClick={() => createMut.mutate()}>{createMut.isPending ? 'Salvando…' : 'Salvar'}</button>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setErr(null) }}>Cancelar</button>
          </div>
        </div>
      )}

      {isLoading ? <PageLoading /> : exames.length === 0 ? (
        <EmptyState msg="Nenhum exame registrado." />
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Data</th>
                <th>Validade</th>
                <th>Resultado</th>
                <th>Médico</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(exames as FuncionarioExame[]).map(e => {
                const res = RESULTADOS_EXAME.find(r => r.value === e.resultado)
                return (
                  <tr key={e.id}>
                    <td style={{ fontSize: 'var(--font-size-sm)' }}>{TIPOS_EXAME.find(t => t.value === e.tipo)?.label ?? e.tipo}</td>
                    <td style={{ fontSize: 'var(--font-size-sm)' }}>{new Date(e.data_exame + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                    <td style={{ fontSize: 'var(--font-size-sm)' }}>
                      {e.data_vencimento ? (
                        <span style={{ color: e.vencido ? 'var(--color-danger)' : 'inherit' }}>
                          {new Date(e.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                          {e.vencido && ' ⚠'}
                        </span>
                      ) : '—'}
                    </td>
                    <td><span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: res?.color }}>{res?.label ?? e.resultado}</span></td>
                    <td style={{ fontSize: 'var(--font-size-sm)' }}>{e.medico ?? '—'}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', padding: '2px 6px' }}
                        onClick={() => { if (confirm('Remover exame?')) deleteMut.mutate(e.id) }}>✕</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Step 5: Dados Adicionais ──────────────────────────────────────────────────

function Step5({ funcId }: { funcId: string | null }) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'dependentes' | 'atestados'>('dependentes')

  const { data: dependentes = [], isLoading: loadingDeps } = useQuery({
    queryKey: ['func-deps', funcId],
    queryFn: () => funcApi.dependentes.list(funcId!),
    enabled: !!funcId,
  })
  const { data: atestados = [], isLoading: loadingAtes } = useQuery({
    queryKey: ['func-atestados', funcId],
    queryFn: () => funcApi.atestados.list(funcId!),
    enabled: !!funcId,
  })

  const [showDepForm, setShowDepForm] = useState(false)
  const [depForm, setDepForm] = useState(DEP_EMPTY)
  const [showAteForm, setShowAteForm] = useState(false)
  const [ateForm, setAteForm] = useState(ATE_EMPTY)
  const [err, setErr] = useState<string | null>(null)

  const createDepMut = useMutation({
    mutationFn: () => funcApi.dependentes.create(funcId!, {
      nome: depForm.nome, parentesco: depForm.parentesco,
      data_nascimento: depForm.data_nascimento || undefined,
      cpf: depForm.cpf ? Number(depForm.cpf) : undefined,
      ir_declarado: depForm.ir_declarado,
    } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['func-deps', funcId] }); setShowDepForm(false); setDepForm(DEP_EMPTY) },
    onError: e => setErr(getErrorMessage(e)),
  })

  const deleteDepMut = useMutation({
    mutationFn: (id: string) => funcApi.dependentes.delete(funcId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['func-deps', funcId] }),
  })

  const createAteMut = useMutation({
    mutationFn: () => funcApi.atestados.create(funcId!, {
      data_inicio: ateForm.data_inicio,
      data_fim: ateForm.data_fim,
      dias_afastamento: Number(ateForm.dias_afastamento),
      tipo: ateForm.tipo,
      cid: ateForm.cid || undefined,
      medico: ateForm.medico || undefined,
      crm: ateForm.crm || undefined,
      observacao: ateForm.observacao || undefined,
    } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['func-atestados', funcId] }); setShowAteForm(false); setAteForm(ATE_EMPTY) },
    onError: e => setErr(getErrorMessage(e)),
  })

  const deleteAteMut = useMutation({
    mutationFn: (id: string) => funcApi.atestados.delete(funcId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['func-atestados', funcId] }),
  })

  if (!funcId) return <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-gray-400)' }}>Salve os dados básicos primeiro.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {err && <div className="alert alert-danger" style={{ fontSize: 'var(--font-size-sm)' }}>{err}<button style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setErr(null)}>✕</button></div>}

      <div style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: '1px solid var(--color-gray-200)', paddingBottom: 0 }}>
        {(['dependentes', 'atestados'] as const).map(t => (
          <button key={t} type="button" onClick={() => setTab(t)} style={{ padding: 'var(--space-2) var(--space-4)', fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--color-primary)' : 'var(--color-gray-500)', borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textTransform: 'capitalize' }}>
            {t === 'dependentes' ? `Dependentes (${(dependentes as FuncionarioDependente[]).length})` : `Atestados (${(atestados as FuncionarioAtestado[]).length})`}
          </button>
        ))}
      </div>

      {tab === 'dependentes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={{ fontSize: 'var(--font-size-sm)' }} onClick={() => setShowDepForm(true)}>+ Adicionar dependente</button>
          </div>

          {showDepForm && (
            <div className="card" style={{ border: '1px solid var(--color-primary)' }}>
              <h4 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>Novo dependente</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <F label="Nome" span2>
                  <input className="input" value={depForm.nome} onChange={e => setDepForm(f => ({ ...f, nome: e.target.value }))} required />
                </F>
                <F label="Parentesco">
                  <select className="input" value={depForm.parentesco} onChange={e => setDepForm(f => ({ ...f, parentesco: e.target.value }))}>
                    {PARENTESCOS.map(p => <option key={p} value={p}>{PARENTESCO_LABELS[p] ?? p}</option>)}
                  </select>
                </F>
                <F label="Data de nascimento" opt>
                  <input className="input" type="date" value={depForm.data_nascimento} onChange={e => setDepForm(f => ({ ...f, data_nascimento: e.target.value }))} />
                </F>
                <F label="CPF" opt>
                  <input className="input" value={depForm.cpf} onChange={e => setDepForm(f => ({ ...f, cpf: e.target.value.replace(/\D/g, '') }))} maxLength={11} placeholder="Somente números" />
                </F>
                <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <input type="checkbox" id="ir" checked={depForm.ir_declarado} onChange={e => setDepForm(f => ({ ...f, ir_declarado: e.target.checked }))} />
                  <label htmlFor="ir" style={{ fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>Declarado no Imposto de Renda</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <button className="btn btn-primary" disabled={createDepMut.isPending} onClick={() => createDepMut.mutate()}>{createDepMut.isPending ? 'Salvando…' : 'Salvar'}</button>
                <button className="btn btn-ghost" onClick={() => { setShowDepForm(false); setDepForm(DEP_EMPTY) }}>Cancelar</button>
              </div>
            </div>
          )}

          {loadingDeps ? <PageLoading /> : dependentes.length === 0 ? (
            <EmptyState msg="Nenhum dependente cadastrado." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {(dependentes as FuncionarioDependente[]).map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{d.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                      {PARENTESCO_LABELS[d.parentesco] ?? d.parentesco}
                      {d.data_nascimento && ` · ${new Date(d.data_nascimento + 'T00:00:00').toLocaleDateString('pt-BR')}`}
                      {d.cpf && ` · CPF ${formatCpf(d.cpf)}`}
                      {d.ir_declarado && <span style={{ marginLeft: 6, color: 'var(--color-success)', fontWeight: 600 }}>IR ✓</span>}
                    </div>
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', padding: '2px 6px' }}
                    onClick={() => { if (confirm(`Remover ${d.nome}?`)) deleteDepMut.mutate(d.id) }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'atestados' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={{ fontSize: 'var(--font-size-sm)' }} onClick={() => setShowAteForm(true)}>+ Registrar atestado</button>
          </div>

          {showAteForm && (
            <div className="card" style={{ border: '1px solid var(--color-primary)' }}>
              <h4 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>Novo atestado</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <F label="Tipo">
                  <select className="input" value={ateForm.tipo} onChange={e => setAteForm(f => ({ ...f, tipo: e.target.value as TipoAtestado }))}>
                    {TIPOS_ATESTADO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </F>
                <F label="Dias afastamento">
                  <input className="input" type="number" min="1" value={ateForm.dias_afastamento} onChange={e => setAteForm(f => ({ ...f, dias_afastamento: e.target.value }))} />
                </F>
                <F label="Data início">
                  <input className="input" type="date" value={ateForm.data_inicio} onChange={e => setAteForm(f => ({ ...f, data_inicio: e.target.value }))} required />
                </F>
                <F label="Data fim">
                  <input className="input" type="date" value={ateForm.data_fim} onChange={e => setAteForm(f => ({ ...f, data_fim: e.target.value }))} required />
                </F>
                <F label="CID" opt>
                  <input className="input" value={ateForm.cid} onChange={e => setAteForm(f => ({ ...f, cid: e.target.value }))} placeholder="Ex: J00, M54" />
                </F>
                <F label="Médico" opt>
                  <input className="input" value={ateForm.medico} onChange={e => setAteForm(f => ({ ...f, medico: e.target.value }))} />
                </F>
                <F label="CRM" opt>
                  <input className="input" value={ateForm.crm} onChange={e => setAteForm(f => ({ ...f, crm: e.target.value }))} placeholder="000000-SP" />
                </F>
                <F label="Observação" opt>
                  <input className="input" value={ateForm.observacao} onChange={e => setAteForm(f => ({ ...f, observacao: e.target.value }))} />
                </F>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <button className="btn btn-primary" disabled={createAteMut.isPending} onClick={() => createAteMut.mutate()}>{createAteMut.isPending ? 'Salvando…' : 'Salvar'}</button>
                <button className="btn btn-ghost" onClick={() => { setShowAteForm(false); setAteForm(ATE_EMPTY) }}>Cancelar</button>
              </div>
            </div>
          )}

          {loadingAtes ? <PageLoading /> : atestados.length === 0 ? (
            <EmptyState msg="Nenhum atestado registrado." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {(atestados as FuncionarioAtestado[]).map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-3)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 2 }}>
                      <span className="badge badge-gray" style={{ fontSize: 'var(--font-size-xs)' }}>{TIPOS_ATESTADO.find(t => t.value === a.tipo)?.label ?? a.tipo}</span>
                      <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{a.dias_afastamento} dia(s)</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                      {new Date(a.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(a.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                      {a.cid && ` · CID: ${a.cid}`}
                      {a.medico && ` · Dr. ${a.medico}`}
                    </div>
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', padding: '2px 6px' }}
                    onClick={() => { if (confirm('Remover atestado?')) deleteAteMut.mutate(a.id) }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────

export function FuncionariosPage() {
  const qc = useQueryClient()
  const [apenasAtivos, setApenasAtivos] = useState(true)

  const { data: funcs, isLoading } = useQuery({
    queryKey: ['funcionarios', apenasAtivos],
    queryFn: () => funcApi.list(apenasAtivos),
  })
  const { data: politicas = [] } = useQuery({ queryKey: ['politicas'], queryFn: politicaApi.list })
  const { data: escalas = [] } = useQuery({ queryKey: ['escalas'], queryFn: () => escalaApi.list(false) })
  const { data: empresas = [] } = useQuery({ queryKey: ['empresas'], queryFn: empresaApi.list })

  const [showWizard, setShowWizard] = useState(false)
  const [wizardStep, setWizardStep] = useState(1)
  const [editFunc, setEditFunc] = useState<Funcionario | null>(null)
  const [savedFuncId, setSavedFuncId] = useState<string | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [formError, setFormError] = useState<string | null>(null)
  // Conflito de PIS: funcionário já existe (pode estar inativo)
  const [pisConflito, setPisConflito] = useState<{ id: string; nome: string; ativo: boolean } | null>(null)

  function openCreate() {
    setEditFunc(null)
    setSavedFuncId(null)
    setForm(FORM_EMPTY)
    setFormError(null)
    setWizardStep(1)
    setShowWizard(true)
  }

  function openEdit(f: Funcionario) {
    setEditFunc(f)
    setSavedFuncId(f.id)
    setForm({
      nome: f.nome, cpf: String(f.cpf), pis: f.pis ? String(f.pis) : '',
      empresa_id: f.empresa_id ?? '', rg: f.rg ?? '', cracha: f.cracha ?? '',
      registro_folha: f.registro_folha ?? '', celular: f.celular ?? '',
      email: f.email ?? '', matricula: f.matricula ?? '', cargo: f.cargo ?? '',
      departamento: f.departamento ?? '', local_trabalho: f.local_trabalho ?? '',
      data_admissao: f.data_admissao, data_demissao: f.data_demissao ?? '',
      aviso_previo_data: f.aviso_previo_data ?? '',
      jornada_horas_dia: String(f.jornada_horas_dia),
      tipo_contrato: f.tipo_contrato, politica_id: f.politica_id ?? '',
      escala_id: f.escala_id ?? '', ativo: f.ativo,
    })
    setFormError(null)
    setWizardStep(1)
    setShowWizard(true)
  }

  function closeWizard() {
    setShowWizard(false)
    setEditFunc(null)
    setSavedFuncId(null)
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = {
        nome: form.nome,
        cpf: Number(form.cpf),
        pis: Number(form.pis),
        empresa_id: form.empresa_id,
        rg: form.rg || null,
        cracha: form.cracha || null,
        registro_folha: form.registro_folha || null,
        celular: form.celular || null,
        email: form.email || null,
        matricula: form.matricula || null,
        cargo: form.cargo || null,
        departamento: form.departamento || null,
        local_trabalho: form.local_trabalho || null,
        data_admissao: form.data_admissao,
        data_demissao: form.data_demissao || null,
        aviso_previo_data: form.aviso_previo_data || null,
        jornada_horas_dia: Number(form.jornada_horas_dia),
        tipo_contrato: form.tipo_contrato,
        politica_id: form.politica_id || null,
        escala_id: form.escala_id || null,
        ativo: form.ativo,
      }
      return editFunc
        ? funcApi.update(editFunc.id, data)
        : funcApi.create(data as any)
    },
    onSuccess: (result: any) => {
      qc.invalidateQueries({ queryKey: ['funcionarios'] })
      const id = result?.id ?? editFunc?.id
      if (id) setSavedFuncId(id)
      if (!editFunc) {
        setEditFunc({ ...form, id, cpf: Number(form.cpf), pis: Number(form.pis) } as any)
      }
      setFormError(null)
      setPisConflito(null)
      setWizardStep(s => Math.min(s + 1, 5))
    },
    onError: (err: any) => {
      // Trata 409 estruturado do backend
      const detail = err?.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'PIS_DUPLICADO') {
        setPisConflito({ id: detail.id, nome: detail.nome, ativo: detail.ativo })
        return
      }
      setPisConflito(null)
      if (isPlanLimitError(err)) setFormError(getErrorMessage(err))
      else if (detail?.code === 'CPF_DUPLICADO' || getErrorMessage(err).includes('CPF')) setFormError('CPF já cadastrado neste tenant.')
      else setFormError(getErrorMessage(err))
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => funcApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['funcionarios'] }),
  })

  const reativarMutation = useMutation({
    mutationFn: (id: string) => funcApi.reativar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['funcionarios'] }),
  })

  // Reativar a partir do modal de conflito PIS
  const reativarConflitoPisMutation = useMutation({
    mutationFn: (id: string) => funcApi.reativar(id),
    onSuccess: (reativado: any) => {
      qc.invalidateQueries({ queryKey: ['funcionarios'] })
      setPisConflito(null)
      // Abre o wizard de edição para o funcionário reativado
      openEdit(reativado)
    },
    onError: () => setFormError('Erro ao reativar o funcionário.'),
  })

  const empresasAtivas = (empresas as any[]).filter((e: any) => e.ativo)

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Funcionários</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Cadastro completo com horário, cargos, exames e dependentes
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <label style={{ fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={apenasAtivos} onChange={e => setApenasAtivos(e.target.checked)} />
            Só ativos
          </label>
          <button className="btn btn-primary" onClick={openCreate}>+ Novo Funcionário</button>
        </div>
      </div>

      {/* Modal de conflito de PIS ──────────────────────────────────────────── */}
      {pisConflito && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div className="card" style={{ width: '100%', maxWidth: 460 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <div>
                <h3 style={{ fontWeight: 700, marginBottom: 4 }}>PIS já cadastrado</h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                  Este PIS pertence a <b>{pisConflito.nome}</b>, que está
                  {pisConflito.ativo
                    ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}> ativo</span>
                    : <span style={{ color: 'var(--color-gray-500)', fontWeight: 600 }}> inativo</span>
                  }.
                </p>
              </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
              <b>LGPD — Art. 6°, princípio da qualidade dos dados:</b> Cada PIS/NIT identifica unicamente um trabalhador.
              Não é permitido cadastrar duas entradas para o mesmo titular.
              {!pisConflito.ativo && ' O cadastro inativo pode ser reativado para este funcionário.'}
            </div>

            {pisConflito.ativo ? (
              <div>
                <p style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
                  Para editar os dados deste funcionário, feche esta janela e use o botão <b>Editar</b> na lista.
                </p>
                <button className="btn btn-primary" onClick={() => setPisConflito(null)}>Entendido</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <p style={{ fontSize: 'var(--font-size-sm)' }}>
                  Deseja <b>reativar</b> o cadastro de <b>{pisConflito.nome}</b>?
                  Isso reabrirá o wizard de edição com todos os dados históricos preservados.
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <button
                    className="btn btn-primary"
                    disabled={reativarConflitoPisMutation.isPending}
                    onClick={() => reativarConflitoPisMutation.mutate(pisConflito.id)}
                  >
                    {reativarConflitoPisMutation.isPending ? 'Reativando…' : 'Reativar e editar'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setPisConflito(null)}>Cancelar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wizard modal */}
      {showWizard && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 50, overflowY: 'auto', padding: 'var(--space-6) var(--space-4)' }}
          onClick={e => { if (e.target === e.currentTarget) closeWizard() }}>
          <div className="card" style={{ width: '100%', maxWidth: 740, minHeight: 500 }}>
            {/* Wizard header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontWeight: 700, fontSize: 'var(--font-size-xl)' }}>
                {editFunc ? `Funcionário — ${editFunc.nome}` : 'Novo Funcionário'}
              </h2>
              <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-lg)', padding: '0 var(--space-2)' }} onClick={closeWizard}>✕</button>
            </div>

            <WizardNav step={wizardStep} onChange={setWizardStep} />

            {formError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>{formError}</div>}

            {/* Steps */}
            {wizardStep === 1 && (
              <Step1 form={form} setForm={setForm} isEdit={!!editFunc} empresas={empresasAtivas} />
            )}
            {wizardStep === 2 && (
              <Step2 form={form} setForm={setForm} politicas={politicas as any} escalas={escalas as any} funcId={savedFuncId} />
            )}
            {wizardStep === 3 && (
              <Step3 funcId={savedFuncId} empresas={empresasAtivas} />
            )}
            {wizardStep === 4 && (
              <Step4 funcId={savedFuncId} />
            )}
            {wizardStep === 5 && (
              <Step5 funcId={savedFuncId} />
            )}

            {/* Footer actions */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                {wizardStep > 1 && (
                  <button type="button" className="btn btn-ghost" onClick={() => setWizardStep(s => s - 1)}>◀ Anterior</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                {wizardStep <= 2 && (
                  <button type="button" className="btn btn-primary" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                    {saveMutation.isPending ? 'Salvando…' : savedFuncId ? 'Salvar e Avançar ▶' : 'Salvar e Continuar ▶'}
                  </button>
                )}
                {wizardStep > 2 && wizardStep < 5 && (
                  <button type="button" className="btn btn-ghost" onClick={() => setWizardStep(s => s + 1)}>Próximo ▶</button>
                )}
                {wizardStep === 5 && (
                  <button type="button" className="btn btn-primary" onClick={closeWizard}>Concluir</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="card" style={{ padding: isLoading || !funcs?.length ? 'var(--space-6)' : 0 }}>
        {isLoading ? <PageLoading /> : !funcs?.length ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
            <p style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
              {apenasAtivos ? 'Nenhum funcionário ativo.' : 'Nenhum funcionário cadastrado.'}
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Empresa</th>
                  <th>Cargo</th>
                  <th>Contrato</th>
                  <th>Jornada</th>
                  <th>Admissão</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {funcs.map(f => {
                  const emp = (empresas as any[]).find((e: any) => e.id === f.empresa_id)
                  return (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 500 }}>{f.nome}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{formatCpf(f.cpf)}</td>
                      <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>{emp?.nome ?? '—'}</td>
                      <td>{f.cargo ?? '—'}</td>
                      <td><span className="badge badge-gray">{TIPO_CONTRATO_LABELS[f.tipo_contrato] ?? f.tipo_contrato}</span></td>
                      <td>{f.jornada_horas_dia}h/dia</td>
                      <td>{new Date(f.data_admissao + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                      <td>
                        <span className={`badge ${f.ativo ? 'badge-green' : 'badge-gray'}`}>
                          {f.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                          <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)' }} onClick={() => openEdit(f)}>Editar</button>
                          {f.ativo ? (
                            <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)', color: 'var(--color-danger)' }}
                              onClick={() => { if (confirm(`Desativar ${f.nome}?`)) deactivateMutation.mutate(f.id) }}>
                              Desativar
                            </button>
                          ) : (
                            <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)', color: 'var(--color-success)' }}
                              onClick={() => reativarMutation.mutate(f.id)}>
                              Reativar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
